/**
 * AcceptInvite.tsx
 *
 * onboarding/pilot-v1 — Invitation acceptance page.
 * Route: /accept-invite?token=<token>
 *
 * Flow:
 *   1. On mount, validate token via GET /api/invite/validate?token=<token>
 *   2. Pre-fill email and name from invitation record
 *   3. User sets password and confirms full name
 *   4. POST /api/invite/accept — creates auth user + public.users row in T3
 *   5. Redirect to /login so the user signs in normally (triggers /onboarding on first login)
 *
 * Canon:
 *   - No exclamation points, no emojis.
 *   - 5-color palette: gray #6B7280, red #DC2626, orange #EA580C, yellow #EAB308, green #16A34A
 *   - Bryan K. Short, Founder and CEO, Mi6 Group.
 */

import { useState, useEffect } from "react";
import { useLocation, useSearch } from "wouter";

const C = {
  black:   "#0B0B0F",
  softBg:  "#FAFAF7",
  red:     "#DC2626",
  orange:  "#EA580C",
  green:   "#16A34A",
  gray:    "#6B7280",
  gray200: "#E5E7EB",
  gray50:  "#F9FAFB",
  white:   "#FFFFFF",
} as const;

interface InviteData {
  email: string;
  role: string;
  name: string;
  tenant_id: number;
}

export default function AcceptInvite() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const token = params.get("token") || "";

  const [inviteState, setInviteState] = useState<
    "loading" | "valid" | "invalid" | "expired" | "already_accepted"
  >("loading");
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [invalidReason, setInvalidReason] = useState("");

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Validate the token on mount
  useEffect(() => {
    if (!token) {
      setInviteState("invalid");
      setInvalidReason("No invitation token found in this link.");
      return;
    }

    fetch(`/api/invite/validate?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) {
          setInvite({
            email: data.email,
            role: data.role,
            name: data.name,
            tenant_id: data.tenant_id,
          });
          setFullName(data.name || "");
          setInviteState("valid");
        } else {
          const reason = data.reason || "Invalid invitation";
          if (reason.toLowerCase().includes("expired")) setInviteState("expired");
          else if (reason.toLowerCase().includes("accepted")) setInviteState("already_accepted");
          else setInviteState("invalid");
          setInvalidReason(reason);
        }
      })
      .catch(() => {
        setInviteState("invalid");
        setInvalidReason("Could not verify invitation. Check your connection and try again.");
      });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!fullName.trim() || fullName.trim().length < 2) {
      setError("Please enter your full name.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const res = await fetch("/api/invite/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password, full_name: fullName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Could not accept invitation. Please try again.");
        return;
      }
      setDone(true);
      // Give the user a moment to read the success message, then route to login
      setTimeout(() => navigate("/login"), 2200);
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (inviteState === "loading") {
    return (
      <Shell>
        <div style={{ textAlign: "center", color: C.gray }}>
          Verifying your invitation...
        </div>
      </Shell>
    );
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <Shell>
        <div style={{ textAlign: "center", maxWidth: 420 }}>
          <div
            style={{
              width: 52, height: 52, borderRadius: "50%",
              background: C.green + "20", border: `1.5px solid ${C.green}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M5 12l5 5L19 7" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.black, marginBottom: 12 }}>
            Account created
          </h1>
          <p style={{ fontSize: 14, color: C.gray, lineHeight: 1.6 }}>
            Redirecting you to sign in...
          </p>
        </div>
      </Shell>
    );
  }

  // ── Invalid / expired / already accepted ──────────────────────────────────
  if (inviteState !== "valid") {
    const isExpired = inviteState === "expired";
    const isAccepted = inviteState === "already_accepted";
    return (
      <Shell>
        <div style={{ textAlign: "center", maxWidth: 420, padding: "0 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.red, marginBottom: 12 }}>
            {isExpired ? "Link Expired" : isAccepted ? "Already Accepted" : "Invalid Link"}
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.black, marginBottom: 12 }}>
            {isExpired
              ? "This invitation link has expired."
              : isAccepted
              ? "This invitation has already been used."
              : "This invitation link is not valid."}
          </h1>
          <p style={{ fontSize: 14, color: C.gray, lineHeight: 1.6, marginBottom: 24 }}>
            {isAccepted
              ? "If you already set up your account, sign in below."
              : "Contact Bryan K. Short at bryan.short@mi6group.com to request a new invitation."}
          </p>
          {isAccepted && (
            <a
              href="/#/login"
              style={{
                display: "inline-block", padding: "11px 32px",
                background: C.black, color: C.white,
                borderRadius: 6, fontSize: 14, fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Sign in
            </a>
          )}
          <p style={{ fontSize: 11, color: C.gray, marginTop: 16 }}>
            Reason: {invalidReason}
          </p>
        </div>
      </Shell>
    );
  }

  // ── Valid invitation \u2014 show signup form ─────────────────────────────────────
  return (
    <Shell>
      <div style={{ width: "100%", maxWidth: 460, padding: "0 16px" }}>
        {/* Header */}
        <div style={{ marginBottom: 28, textAlign: "center" }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", textTransform: "uppercase", color: C.orange, marginBottom: 8 }}>
            Restricted Pilot
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: C.black, marginBottom: 8, lineHeight: 1.3 }}>
            Accept your invitation
          </h1>
          <p style={{ fontSize: 13, color: C.gray, lineHeight: 1.55 }}>
            You have been invited to join Mi6 AIP. Set your password to complete account setup.
          </p>
        </div>

        {/* Pilot disclaimer */}
        <div
          style={{
            background: "#FFF7ED", border: `1.5px solid ${C.orange}`,
            borderRadius: 8, padding: "14px 18px", marginBottom: 24,
            fontSize: 12, color: C.black, lineHeight: 1.6,
          }}
        >
          <strong style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: C.orange, display: "block", marginBottom: 4 }}>
            Pilot notice
          </strong>
          Mi6 AIP is in restricted pilot. The methodology and field kit are production-ready.
          The platform is iterating toward production. Your feedback shapes what we build next.
        </div>

        {/* Form */}
        <div
          style={{
            background: C.white, border: `1px solid ${C.gray200}`,
            borderRadius: 10, padding: "28px 28px 24px",
          }}
        >
          {/* Email (read-only from invite) */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: C.gray, marginBottom: 6 }}>
              Email
            </label>
            <div
              style={{
                padding: "10px 12px", background: C.gray50,
                border: `1px solid ${C.gray200}`, borderRadius: 6,
                fontSize: 14, color: C.black,
              }}
            >
              {invite?.email}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Full name */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: C.gray, marginBottom: 6 }}>
                Full name
              </label>
              <input
                type="text"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Jane Smith"
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "10px 12px", border: `1px solid ${C.gray200}`,
                  borderRadius: 6, fontSize: 14, color: C.black,
                  background: C.white, outline: "none",
                }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: C.gray, marginBottom: 6 }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Min 8 characters"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "10px 48px 10px 12px", border: `1px solid ${C.gray200}`,
                    borderRadius: 6, fontSize: 14, color: C.black,
                    background: C.white, outline: "none",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: 11, fontWeight: 700, color: C.gray, letterSpacing: "0.06em", textTransform: "uppercase",
                  }}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: C.gray, marginBottom: 6 }}>
                Confirm password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  width: "100%", boxSizing: "border-box",
                  padding: "10px 12px", border: `1px solid ${C.gray200}`,
                  borderRadius: 6, fontSize: 14, color: C.black,
                  background: C.white, outline: "none",
                }}
              />
            </div>

            {error && (
              <div
                style={{
                  background: "#FEF2F2", border: `1px solid #FECACA`,
                  borderRadius: 6, padding: "10px 14px",
                  fontSize: 13, color: C.red, marginBottom: 16,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              style={{
                width: "100%", padding: "12px 0",
                background: busy ? C.gray : C.black,
                color: C.white, border: "none",
                borderRadius: 6, fontSize: 14, fontWeight: 600,
                cursor: busy ? "default" : "pointer",
                opacity: busy ? 0.7 : 1,
                transition: "opacity 0.15s",
              }}
            >
              {busy ? "Creating your account..." : "Create account"}
            </button>
          </form>
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: C.gray, marginTop: 20, lineHeight: 1.5 }}>
          Already have an account?{" "}
          <a href="/#/login" style={{ color: C.black, textDecoration: "underline" }}>
            Sign in
          </a>
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "#FAFAF7",
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px",
      }}
    >
      {/* Top brand */}
      <div style={{ marginBottom: 28, textAlign: "center" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "#6B7280" }}>
          Mi6 Group Intelligence Platform
        </div>
      </div>
      {children}
    </div>
  );
}
